import React, { useMemo } from "react";
import { Eye } from "lucide-react";
import StudentLayout from "../../layouts/StudentLayout";
import StudentHeaderSection from "../../components/student/StudentHeaderSection";
import { useGetMyCertificatesQuery } from "../../services/certificate";

const formatCertificateDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "active") {
    return "bg-[#DCFCE7] text-[#166534]";
  }

  if (normalized === "revoked") {
    return "bg-[#FEE2E2] text-[#B91C1C]";
  }

  return "bg-[#F3F4F6] text-[#4B5563]";
};

const Certificates = () => {
  const {
    data: certificatesResponse,
    isLoading,
    isError,
    error,
  } = useGetMyCertificatesQuery();

  const certificates = useMemo(() => {
    const data = certificatesResponse?.data;
    return Array.isArray(data) ? data : [];
  }, [certificatesResponse?.data]);

  const emptyMessage =
    certificatesResponse?.message || "No certificates found yet.";

  const handleViewCertificate = (certificate) => {
    const certificateId = certificate?._id;
    if (!certificateId) return;

    window.open(
      `/certificate/verify/${certificateId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <StudentLayout>
      <div className="hidden sm:block">
        <StudentHeaderSection isBtnIcon={false} btnText="Logout" />
      </div>

      <div className="px-5 pb-16 pt-11 font-['Roboto'] lg:px-12 2xl:px-[70px]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-[-1%] text-[#1D2026]">
              Certification
            </h2>
            <p className="mt-2 text-sm text-[#4E5566]">
              View and download your earned course certificates.
            </p>
          </div>

          {!isLoading && !isError && certificates.length > 0 ? (
            <p className="text-sm text-[#6B7280]">
              {certificatesResponse?.pagination?.total ?? certificates.length}{" "}
              certificate
              {(certificatesResponse?.pagination?.total ?? certificates.length) === 1
                ? ""
                : "s"}
            </p>
          ) : null}
        </div>

        {isLoading ? (
          <p className="mt-8 text-center text-[#6B7280]">
            Loading certificates...
          </p>
        ) : null}

        {isError ? (
          <p className="mt-8 text-center text-[#EF4444]">
            {error?.data?.message || "Could not load certificates."}
          </p>
        ) : null}

        {!isLoading && !isError && certificates.length === 0 ? (
          <div className="mt-8 rounded-lg border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-6 py-12 text-center">
            <p className="text-base font-medium text-[#4B5563]">
              {emptyMessage}
            </p>
            <p className="mt-2 text-sm text-[#9CA3AF]">
              Complete a course and generate your certificate to see it here.
            </p>
          </div>
        ) : null}

        {!isLoading && !isError && certificates.length > 0 ? (
          <>
            <div className="mt-6 space-y-4 md:hidden">
              {certificates.map((certificate) => (
                <article
                  key={certificate._id}
                  className="rounded-lg border border-[#E5E7EB] bg-white p-4 shadow-[0_1px_2px_0_#0000000D]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-[#1F2937]">
                        {certificate.courseTitle || "Course"}
                      </h3>
                      <p className="mt-1 text-sm text-[#6B7280]">
                        {certificate.instructorName || "—"}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium capitalize ${getStatusBadgeClass(certificate.status)}`}
                    >
                      {certificate.status || "—"}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[#9CA3AF]">Grade</p>
                      <p className="mt-0.5 font-medium text-[#1F2937]">
                        {certificate.grade || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#9CA3AF]">Completed</p>
                      <p className="mt-0.5 font-medium text-[#1F2937]">
                        {formatCertificateDate(certificate.completionDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#9CA3AF]">Issued</p>
                      <p className="mt-0.5 font-medium text-[#1F2937]">
                        {formatCertificateDate(certificate.issueDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#9CA3AF]">Student</p>
                      <p className="mt-0.5 font-medium text-[#1F2937]">
                        {certificate.studentName || "—"}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleViewCertificate(certificate)}
                    disabled={!certificate._id}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF6636] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#e65a2e] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Eye size={16} />
                    View Certificate
                  </button>
                </article>
              ))}
            </div>

            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white shadow-[0_1px_2px_0_#0000000D] md:block">
              <table className="min-w-[900px] w-full">
                <thead>
                  <tr className="border-b border-[#E5E7EB] text-left text-xs font-semibold uppercase tracking-[0.6px] text-[#606161]">
                    <th className="px-4 py-3 font-medium">Course</th>
                    <th className="px-4 py-3 font-medium">Instructor</th>
                    <th className="px-4 py-3 font-medium">Grade</th>
                    <th className="px-4 py-3 font-medium">Completed</th>
                    <th className="px-4 py-3 font-medium">Issued</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((certificate) => (
                    <tr
                      key={certificate._id}
                      className="border-b border-[#F3F4F6] last:border-b-0"
                    >
                      <td className="px-4 py-4 align-middle">
                        <p className="text-sm font-semibold text-[#1F2937]">
                          {certificate.courseTitle || "—"}
                        </p>
                        <p className="mt-0.5 text-xs text-[#9CA3AF]">
                          {certificate.studentName || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-4 align-middle text-sm text-[#4B5563]">
                        {certificate.instructorName || "—"}
                      </td>
                      <td className="px-4 py-4 align-middle text-sm font-medium text-[#1F2937]">
                        {certificate.grade || "—"}
                      </td>
                      <td className="px-4 py-4 align-middle text-sm text-[#4B5563] whitespace-nowrap">
                        {formatCertificateDate(certificate.completionDate)}
                      </td>
                      <td className="px-4 py-4 align-middle text-sm text-[#4B5563] whitespace-nowrap">
                        {formatCertificateDate(certificate.issueDate)}
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${getStatusBadgeClass(certificate.status)}`}
                        >
                          {certificate.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <button
                          type="button"
                          onClick={() => handleViewCertificate(certificate)}
                          disabled={!certificate._id}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[#FF6636] px-3 py-1.5 text-sm font-medium text-[#FF6636] transition-colors hover:bg-[#FF66360D] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Eye size={15} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </StudentLayout>
  );
};

export default Certificates;
